// Artist / Album / Playlist detail — provider-neutral view over MusicProvider.getArtist/
// getAlbum/getPlaylist. Renders a header (artwork, meta, Play/Shuffle) + track list / sections
// with per-track Play, Lyrics, Add-to-Challenge, and Open. No MusicKit in this component.
import React, { useEffect, useState } from 'react';
import { ArrowLeft, Play, Shuffle, Quote, ExternalLink, Plus, Music2, Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { openProviderUrl } from '@/lib/providerLinks';
import { appleMusicProvider } from './lib/appleMusicProvider';
import { usePlayer } from './player/usePlayer';
import type { MusicItem, MusicAlbumDetails, MusicArtistDetails, MusicPlaylistDetails } from './lib/types';

const provider = appleMusicProvider();
const fmt = (ms?: number) => (ms ? `${Math.floor(ms / 60000)}:${String(Math.round((ms % 60000) / 1000)).padStart(2, '0')}` : '');

type Details = MusicAlbumDetails | MusicArtistDetails | MusicPlaylistDetails;

export const MusicDetail: React.FC<{ item: MusicItem; onBack: () => void; onAddToChallenge?: (i: MusicItem) => void }> = ({ item, onBack, onAddToChallenge }) => {
  const { playItem, openPanel, shuffle } = usePlayer();
  const [d, setD] = useState<Details | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading'); setD(null);
    const p = item.kind === 'album' ? provider.getAlbum(item.id)
      : item.kind === 'artist' ? provider.getArtist(item.id)
      : provider.getPlaylist(item.id);
    p.then((r) => { if (alive) { setD(r); setState('ready'); } }).catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [item.id, item.kind]);

  const hero = provider.artworkUrl(d?.artwork || item.artwork, 400);
  const playAll = () => playItem(item);
  const shuffleAll = async () => { await shuffle(true); await playItem(item); };

  const tracks: MusicItem[] = d && 'tracks' in d ? d.tracks : d && 'topSongs' in d ? d.topSongs : [];
  const albums: MusicItem[] = d && 'albums' in d ? d.albums : [];

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground focus:outline-none focus-visible:underline">
        <ArrowLeft className="h-4 w-4" />Back
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className={`h-40 w-40 shrink-0 overflow-hidden bg-muted ${item.kind === 'artist' ? 'rounded-full' : 'rounded-2xl'} shadow`}>
          {hero ? <img src={hero} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-10 w-10 text-muted-foreground/40" /></div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{item.kind}</div>
          <h2 className="truncate text-2xl font-bold text-foreground" dir="auto">{d?.title || item.title}</h2>
          <p className="truncate text-sm text-muted-foreground" dir="auto">{metaLine(d, item)}</p>
          {d && 'bio' in d && d.bio && <p className="mt-2 line-clamp-3 max-w-prose text-sm text-muted-foreground">{d.bio}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={playAll}><Play className="mr-1.5 h-4 w-4" />Play</Button>
            <Button size="sm" variant="outline" onClick={shuffleAll}><Shuffle className="mr-1.5 h-4 w-4" />Shuffle</Button>
            {(d?.canonicalUrl || item.canonicalUrl) && (
              <Button size="sm" variant="outline" onClick={() => openProviderUrl((d?.canonicalUrl || item.canonicalUrl)!)}><ExternalLink className="mr-1.5 h-4 w-4" />Open in Apple Music</Button>
            )}
          </div>
        </div>
      </div>

      {state === 'loading' && <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>}
      {state === 'error' && <p className="text-sm text-muted-foreground">Couldn’t load this from Apple Music. <button className="font-medium text-primary-500 hover:underline" onClick={onBack}>Go back</button></p>}

      {state === 'ready' && tracks.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{item.kind === 'artist' ? 'Popular songs' : 'Tracks'}</h3>
          <ul className="space-y-0.5">
            {tracks.map((t, idx) => (
              <li key={t.id}>
                <div className="group flex items-center gap-3 rounded-lg p-2 hover:bg-muted">
                  <span className="w-5 text-center text-xs text-muted-foreground">{idx + 1}</span>
                  <button type="button" aria-label={`Play ${t.title}`} onClick={() => playItem(t)} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-foreground" dir="auto">{t.title}</span>
                      {t.isExplicit && <span title="Explicit" className="rounded bg-muted px-1 text-[9px] font-bold text-muted-foreground">E</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground" dir="auto">{t.artist}</div>
                  </button>
                  <span className="hidden text-xs tabular-nums text-muted-foreground sm:block">{fmt(t.durationMs)}</span>
                  <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                    <RowBtn label="Lyrics" onClick={() => { void playItem(t).then(() => openPanel('lyrics')); }}><Quote className="h-4 w-4" /></RowBtn>
                    {onAddToChallenge && <RowBtn label="Add to challenge" onClick={() => onAddToChallenge(t)}><Plus className="h-4 w-4" /></RowBtn>}
                    {t.canonicalUrl && <RowBtn label="Open in Apple Music" onClick={() => openProviderUrl(t.canonicalUrl!)}><ExternalLink className="h-4 w-4" /></RowBtn>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {state === 'ready' && albums.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Albums</h3>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {albums.map((a) => {
              const art = provider.artworkUrl(a.artwork, 140);
              return (
                <button key={a.id} type="button" onClick={() => playItem(a)} className="w-32 shrink-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                  {art ? <img src={art} alt="" className="h-32 w-32 rounded-lg object-cover" /> : <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-muted"><Music2 className="h-6 w-6 text-muted-foreground" /></div>}
                  <div className="mt-1.5 truncate text-sm font-medium text-foreground" dir="auto">{a.title}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

function metaLine(d: Details | null, item: MusicItem): string {
  if (d && 'releaseDate' in d) {
    const yr = d.releaseDate ? new Date(d.releaseDate).getFullYear() : '';
    return [d.artist, yr, d.genre, d.trackCount ? `${d.trackCount} tracks` : ''].filter(Boolean).join(' · ');
  }
  if (d && 'curator' in d) return [d.curator, d.trackCount ? `${d.trackCount} tracks` : ''].filter(Boolean).join(' · ');
  return item.subtitle || item.artist || '';
}

const RowBtn: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick}
    className="rounded-md p-1.5 text-muted-foreground hover:bg-card hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
    {children}
  </button>
);
